import { useEffect, useRef } from 'react';

const amenities = [
  {
    icon: 'ri-flashlight-line',
    title: 'New Electrical System',
    description: 'Fully upgraded electrical infrastructure with dedicated power generation plant for uninterrupted service.',
    color: 'bg-amber-50',
    iconColor: 'text-amber-600',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Hydropneumatic Water System',
    description: 'Large-capacity storage tank and state-of-the-art hydropneumatic system ensuring constant water pressure.',
    color: 'bg-forest-50',
    iconColor: 'text-forest',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Modern Security',
    description: 'New gated wall with state-of-the-art security system, including secure parking for up to 10 vehicles.',
    color: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    icon: 'ri-building-line',
    title: 'New Roof &amp; Windows',
    description: 'Completely replaced roof and windows ensuring structural integrity, comfort, and energy efficiency.',
    color: 'bg-rose-50',
    iconColor: 'text-rose-600',
  },
  {
    icon: 'ri-t-shirt-line',
    title: 'Commercial Laundry',
    description: 'Large-capacity commercial washer and dryer for impeccable linens and towels throughout your stay.',
    color: 'bg-forest-50',
    iconColor: 'text-forest',
  },
  {
    icon: 'ri-car-line',
    title: 'Toyota Hilux Minibus',
    description: '2013 Toyota Hilux in pristine condition, accommodating 16 passengers for tours and airport transfers.',
    color: 'bg-amber-50',
    iconColor: 'text-amber-600',
  },
  {
    icon: 'ri-wifi-line',
    title: 'High-Speed WiFi',
    description: 'Reliable high-speed internet throughout all rooms and communal areas for your connectivity needs.',
    color: 'bg-slate-50',
    iconColor: 'text-slate-600',
  },
  {
    icon: 'ri-global-line',
    title: 'Online Booking Platform',
    description: 'The website Montebrisa.com with international e-commerce infrastructure for online payments and bookings.',
    color: 'bg-rose-50',
    iconColor: 'text-rose-600',
  },
];

const services = [
  { icon: 'ri-restaurant-line', label: 'Full Breakfast Included' },
  { icon: 'ri-parking-line', label: 'Free Secure Parking' },
  { icon: 'ri-taxi-line', label: 'Airport Transfers' },
  { icon: 'ri-map-pin-2-line', label: 'City & Reserve Tours' },
  { icon: 'ri-translate-2', label: 'Bilingual Staff' },
  { icon: 'ri-smartphone-line', label: 'Concierge Service' },
];

export default function AmenitiesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible');
      }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="amenities" ref={sectionRef} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <div className="inline-flex items-center gap-2 border border-forest/30 text-forest rounded-full px-4 py-1.5 mb-4">
            <i className="ri-star-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">Amenities &amp; Services</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight mb-4">
            Comforts of the 21st Century
          </h2>
          <p className="font-sans text-base text-gray-500 max-w-xl mx-auto">
            A thorough gut renovation ensures that every modern comfort is seamlessly woven into Montebrisa&apos;s 1940s charm.
          </p>
        </div>

        {/* Amenities Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {amenities.map((item, idx) => (
            <div
              key={item.title}
              className={`reveal rounded-xl p-5 border border-cream-dark group hover:border-forest/30 transition-all duration-300 hover:-translate-y-1`}
              style={{ transitionDelay: `${idx * 0.06}s` }}
            >
              <div className={`w-10 h-10 flex items-center justify-center ${item.color} rounded-lg mb-4`}>
                <i className={`${item.icon} ${item.iconColor} text-lg`} />
              </div>
              <h3
                className="font-sans font-semibold text-forest-dark text-sm mb-2"
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
              <p className="font-sans text-xs text-gray-500 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Services strip */}
        <div className="reveal bg-forest rounded-2xl p-6 md:p-8">
          <h3 className="font-serif text-2xl text-white text-center mb-6">Guest Services</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {services.map(svc => (
              <div key={svc.label} className="flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-full">
                  <i className={`${svc.icon} text-cream text-lg`} />
                </div>
                <span className="font-sans text-xs text-white/80 leading-tight">{svc.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
